import { faTrashXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import Typography from "@mui/material/Typography"
import { useMemo, useState } from "react"

import MuiAutoComplete from "components/MuiAutocomplete"
import MuiTable, { MuiTableColumn } from "components/MuiTable"
import MuiTextField from "components/MuiTextField"
import SectionAccordion from "pages/HandoverTakeover/components/SectionAccordion"
import { IProjectSoItem } from "pages/Projects/ProjectTypes"
import BulkUploadPanel from "./BulkUploadPanel"
import useChargersAndMaterials, { ChargerRow, resolveSkuOption } from "./useChargersAndMaterials"

type Hook = ReturnType<typeof useChargersAndMaterials>

type Props = {
    states: Hook["states"]
    functions: Hook["functions"]
}

type SoLineStat = { item: IProjectSoItem; addedHere: number; usedElsewhere: number; available: number }

/**
 * One visible Items-table row. A Charger view-row groups every same-product charger row into one
 * editable-quantity line (chargers stay one-row-per-unit underneath); a Material view-row is a
 * straight 1:1 with its MaterialRow, as before.
 */
type ChargerGroup = { kind: "Charger"; key: string; indices: number[]; row: ChargerRow }
type MaterialViewRow = { kind: "Material"; index: number }
type ViewRow = ChargerGroup | MaterialViewRow

const kindLabel = (kind: "Charger" | "Material") => (kind === "Charger" ? "Charger" : "Non Charger")

const TypePill = ({ kind }: { kind: "Charger" | "Material" }) => (
    <Box
        component="span"
        display="inline-block"
        px={1}
        py={0.25}
        borderRadius="4px"
        fontSize="11px"
        fontWeight={500}
        bgcolor={kind === "Charger" ? "#E8F0FE" : "#F1F2F6"}
        color={kind === "Charger" ? "#1A56DB" : "#404767"}>
        {kindLabel(kind)}
    </Box>
)

const DeleteIcon = ({ onClick }: { onClick: () => void }) => (
    <Box
        width="20px"
        height="20px"
        display="flex"
        justifyContent="center"
        alignItems="center"
        borderRadius="50%"
        bgcolor="#F940541A"
        sx={{ cursor: "pointer" }}
        onClick={onClick}>
        <FontAwesomeIcon icon={faTrashXmark} color="#F94054" size="sm" />
    </Box>
)

const ProjectItemsView = ({ states, functions }: Props) => {
    const [addItemAnchor, setAddItemAnchor] = useState<HTMLElement | null>(null)
    const [bulkUploadOpen, setBulkUploadOpen] = useState(false)
    // Draft text for an in-progress charger quantity edit, keyed by group key. Keeping this local
    // until blur means clearing the field to type a new number doesn't commit "0" mid-edit and
    // delete the row out from under the user.
    const [chargerQtyDrafts, setChargerQtyDrafts] = useState<Record<string, string>>({})

    const chargers = states.formik.values.chargers
    const materials = states.formik.values.materials

    const soLineStats = states.soLineStats as SoLineStat[]
    const totalAvailable = soLineStats.reduce((sum, stat) => sum + stat.available, 0)
    const chargerUnitCount = chargers.length
    const materialUnitCount = materials.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0)

    // Group same-product charger rows into one view-row; a product_id-less (freshly added, not yet
    // picked) row stays its own singleton group so several blank rows don't collapse into one.
    const chargerGroups: ChargerGroup[] = useMemo(() => {
        const order: string[] = []
        const indicesByKey = new Map<string, number[]>()
        chargers.forEach((row, index) => {
            const key = row.product_id ? `p-${row.product_id}` : `row-${index}`
            if (!indicesByKey.has(key)) {
                indicesByKey.set(key, [])
                order.push(key)
            }
            indicesByKey.get(key)!.push(index)
        })
        return order.map(key => {
            const indices = indicesByKey.get(key)!
            return { kind: "Charger" as const, key, indices, row: chargers[indices[0]] }
        })
    }, [chargers])

    const viewRows: ViewRow[] = [
        ...chargerGroups,
        ...materials.map((_, index) => ({ kind: "Material" as const, index })),
    ]

    const soColumns: MuiTableColumn<SoLineStat>[] = [
        { id: "custom", label: "Type", width: 100, render: (_, row) => <TypePill kind={row.item.kind} /> },
        { id: "custom", label: "SKU Code", width: 120, render: (_, row) => row.item.sku_code || "-" },
        { id: "custom", label: "Display Name", width: 220, render: (_, row) => row.item.display_name || "-" },
        { id: "custom", label: "SO Qty", width: 80, render: (_, row) => row.item.ordered_qty },
        { id: "custom", label: "Used in other projects", width: 140, render: (_, row) => row.usedElsewhere },
        { id: "custom", label: "Added here", width: 100, render: (_, row) => row.addedHere },
        { id: "custom", label: "Available", width: 100, render: (_, row) => row.available },
        {
            id: "custom",
            label: "Qty for this project",
            width: 140,
            render: (_, row) => (
                <MuiTextField
                    type="number"
                    size="small"
                    placeholder="0"
                    value={states.qtyToAddDrafts[row.item.product_id] ?? ""}
                    inputProps={{ max: row.available, min: 0 }}
                    disabled={row.available <= 0}
                    onChange={e => functions.setQtyDraft(row.item.product_id, e.target.value)}
                />
            ),
        },
    ]

    const handleAddToProject = () => {
        const entries = soLineStats
            .map(stat => ({ item: stat.item, qty: Number(states.qtyToAddDrafts[stat.item.product_id]) || 0 }))
            .filter(entry => entry.qty > 0)
        if (entries.length) functions.handleAddFromSalesOrder(entries)
    }

    const itemColumns: MuiTableColumn<ViewRow>[] = [
        { id: "index", label: "S.No.", width: 50 },
        { id: "custom", label: "Type", width: 100, render: (_, view) => <TypePill kind={view.kind} /> },
        {
            id: "custom",
            label: "SKU Code",
            width: 160,
            render: (_, view) => {
                const row = view.kind === "Charger" ? view.row : materials[view.index]
                const options = view.kind === "Charger" ? states.chargerSkuOptions : states.materialSkuOptions
                const locked = !!row.fromSalesOrder
                return (
                    <MuiAutoComplete
                        disablePortal={false}
                        size="small"
                        containerHeight="100%"
                        disabled={locked}
                        options={options}
                        value={resolveSkuOption(options, row)}
                        onChange={(e: any) => {
                            const productId = e?.id || ""
                            if (view.kind === "Charger") {
                                view.indices.forEach(index => functions.updateChargerRow(index, "sku_code", productId))
                            } else {
                                functions.updateMaterialRow(view.index, "sku_code", productId)
                            }
                        }}
                    />
                )
            },
        },
        {
            id: "custom",
            label: "Display Name",
            width: 260,
            render: (_, view) => {
                const name = view.kind === "Charger" ? view.row.display_name : materials[view.index].description
                return (
                    <Box padding="4px 8px" textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
                        {name || "-"}
                    </Box>
                )
            },
        },
        {
            id: "custom",
            label: "Quantity*",
            width: 120,
            render: (_, view) => {
                if (view.kind === "Charger") {
                    const hasError = states.formik.submitCount > 0 && view.indices.length === 0
                    const draft = chargerQtyDrafts[view.key]
                    return (
                        <MuiTextField
                            value={draft ?? String(view.indices.length)}
                            onChange={e => setChargerQtyDrafts(prev => ({ ...prev, [view.key]: e.target.value }))}
                            onBlur={() => {
                                const raw = chargerQtyDrafts[view.key]
                                setChargerQtyDrafts(prev => {
                                    const next = { ...prev }
                                    delete next[view.key]
                                    return next
                                })
                                if (raw === undefined) return
                                const parsed = raw.trim() === "" ? NaN : Number(raw)
                                functions.setChargerQuantity(
                                    view.row.product_id,
                                    view.indices,
                                    Number.isFinite(parsed) ? parsed : view.indices.length
                                )
                            }}
                            type="number"
                            size="small"
                            error={hasError}
                        />
                    )
                }
                const row = materials[view.index]
                const hasError = states.formik.submitCount > 0 && (!row.quantity || row.quantity === 0)
                return (
                    <MuiTextField
                        value={row.quantity}
                        onChange={e => functions.updateMaterialRow(view.index, "quantity", e.target.value)}
                        type="number"
                        size="small"
                        error={hasError}
                    />
                )
            },
        },
        {
            id: "custom",
            label: "",
            width: 60,
            render: (_, view) => (
                <Box display="flex" alignItems="center" padding="4px 8px">
                    <DeleteIcon
                        onClick={() =>
                            view.kind === "Charger"
                                ? functions.handleDeleteChargerGroup(view.indices)
                                : functions.handleDeleteMaterialRow(view.index)
                        }
                    />
                </Box>
            ),
        },
    ]

    return (
        <Box display="flex" flexDirection="column" gap={3}>
            {states.selectedPurchaseOrder && soLineStats.length > 0 && (
                <SectionAccordion
                    defaultExpanded
                    header={
                        <Box display="flex" flexDirection="column" gap="2px">
                            <Typography fontWeight={600} color="statiqOrange.main" fontSize="13px">
                                SELECT FROM SALES ORDER · {states.selectedPurchaseOrder.po_number}
                            </Typography>
                            <Typography fontSize="11px" color="#70758D">
                                {states.selectedPurchaseOrder.po_name} · ₹
                                {Number(states.selectedPurchaseOrder.po_amount).toLocaleString("en-IN")} ·{" "}
                                {soLineStats.length} line(s), {totalAvailable} unit(s) available
                            </Typography>
                        </Box>
                    }>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <MuiTable columns={soColumns} rows={soLineStats} showInsideBorders />
                        <Typography fontSize="11px" color="#70758D">
                            Quantities are capped at what is still available on the sales order.
                        </Typography>
                        <Box display="flex" justifyContent="flex-end">
                            <Button variant="contained" color="statiqOrange" onClick={handleAddToProject}>
                                Add to project
                            </Button>
                        </Box>
                    </Box>
                </SectionAccordion>
            )}

            <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                    <Box display="flex" flexDirection="column">
                        <Typography fontSize="14px" variant="label" color="statiqOrange.main">
                            Items ({String(viewRows.length).padStart(2, "0")})
                        </Typography>
                        <Typography fontSize="11px" color="#70758D">
                            {chargerUnitCount} charger unit(s) · {materialUnitCount} non-charger unit(s)
                        </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={states.disableManualItemEntry}
                            onClick={e => setAddItemAnchor(e.currentTarget)}>
                            + Add Item
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={states.disableManualItemEntry}
                            onClick={() => setBulkUploadOpen(prev => !prev)}>
                            Bulk upload
                        </Button>
                    </Box>
                </Box>

                {bulkUploadOpen && (
                    <BulkUploadPanel
                        onClose={() => setBulkUploadOpen(false)}
                        isFetchingChargerTemplate={states.isFetchingChargerTemplate}
                        isFetchingMaterialTemplate={states.isFetchingMaterialTemplate}
                        onDownloadTemplate={functions.handleDownloadTemplate}
                        onParseChargersFile={functions.handleParseChargersFile}
                        onParseMaterialsFile={functions.handleParseMaterialsFile}
                    />
                )}

                <MuiTable columns={itemColumns} rows={viewRows} showInsideBorders />
            </Box>

            <Menu anchorEl={addItemAnchor} open={!!addItemAnchor} onClose={() => setAddItemAnchor(null)}>
                <MenuItem
                    onClick={() => {
                        functions.handleAddBlankItem("Charger")
                        setAddItemAnchor(null)
                    }}>
                    Charger
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        functions.handleAddBlankItem("Material")
                        setAddItemAnchor(null)
                    }}>
                    Non Charger
                </MenuItem>
            </Menu>
        </Box>
    )
}

export default ProjectItemsView
